/* eslint-disable max-lines */
import React, { useMemo, useState } from 'react'
import styled, { css } from 'styled-components'

import { Button, type ButtonStyles } from '@nexiahome/ui-common/components/primitives/buttons'

import CollapsiblePanel from 'components/CollapsiblePanel'

import Table from 'components/Table'

import Api from 'services/api'

import { color } from 'styles/colors'
import { screenSize } from 'styles/shared'

import blobToFileDownload from 'helpers/blobToFileDownload'
import mapLanguageCodeToName from 'helpers/mapLanguageCodeToName'

import { Literature, ProductLiterature } from 'types/ProductLiterature'

import Tooltip from 'components/Tooltip'

import spinner from 'components/Spinner'

type Props = {
  group: ProductLiterature,
  page: 'search' | 'deviceDetails'
}

const ProductLiteratureGroup = ({ group, page }: Props) => {
  const [displayedLiterature, setDisplayedLiterature] = useState(group.literature.slice(0, 10))
  const [isOpen, setIsOpen] = useState(false)
  const [nameSortDirection, setNameSortDirection] = useState<'asc' | 'desc' | null>(null)
  const [downloadingId, setDownloadingId] = useState<string | null>(null)

  const onClickShowMore = () => {
    const nextTenItems = group.literature.slice(displayedLiterature.length, displayedLiterature.length + 10)

    setDisplayedLiterature([...displayedLiterature, ...nextTenItems])
  }

  const onDownloadPdfClick = (literature: Literature) => {
    const documentId = literature.documentName
    setDownloadingId(documentId)
    Api.ProductLiterature.download(literature.downloadUrl)
      .then((res) => {
        blobToFileDownload(res, `${literature.title}.pdf`)
      })
      .catch((error) => {
        console.error('Failed to download literature:', error)
      })
      .finally(() => {
        setDownloadingId((currentId) => currentId === documentId ? null : currentId)
      })
  }

  const onSortByName = () => {
    setNameSortDirection((currentDirection) => (currentDirection === 'asc' ? 'desc' : 'asc'))
  }

  const sortedDisplayedLiterature = useMemo(() => {
    return [...displayedLiterature].sort((a, b) => {
      if (nameSortDirection != null) {
        const nameComparison = a.documentName.localeCompare(b.documentName)

        return nameSortDirection === 'asc' ? nameComparison : -nameComparison
      }

      if (a.current !== b.current) {
        return a.current ? -1 : 1
      }

      const dateA = new Date(a.datePublished).getTime()
      const dateB = new Date(b.datePublished).getTime()
      if (dateA !== dateB) {
        return dateB - dateA
      }

      return a.documentName.localeCompare(b.documentName)
    })
  }, [displayedLiterature, nameSortDirection])

  return (
    <div key={group.literatureType}>
      {isOpen && (
        <S.TableHeader>
          <S.NameHeaderCell title="Name" sortable onSort={onSortByName} sortDirection={nameSortDirection} />
          <S.DescriptionHeaderCell title="Description" />
          <S.LanguageHeaderCell title="Language" />
          <S.CurrentHeaderCell title="Current" />
          <S.DateHeaderCell title="Date Published" />
        </S.TableHeader>
      )}
      <S.CollapsiblePanel
        page={page}
        isOpen={isOpen}
        expandedCallback={() => setIsOpen(true)}
        collapsedCallback={() => setIsOpen(false)}
        headerComponent={
          <S.GroupHeader page={page} $isOpen={isOpen}>
            {group.literatureType} ({group.literature.length})
          </S.GroupHeader>
        }
        caretSize="20px"
      >
        {sortedDisplayedLiterature.map((literature) => (
          <S.Item key={literature.documentName}>
            <S.NameCell>
              <Tooltip title={'Click to view'} placement="top">
                <S.Link
                  href={literature.downloadUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  <S.PictureAsPdfOutlined
                    src={'/img/pdf_icon.svg'}
                    alt={'literature-pdf-icon'}
                  />
                  {literature.documentName}
                </S.Link>
              </Tooltip>
            </S.NameCell>
            <S.DescriptionCell>
              {literature.title}
            </S.DescriptionCell>

            <S.LanguageCell>
              <span>{mapLanguageCodeToName(literature.language)}</span>
            </S.LanguageCell>

            <S.CurrentCell>
              <span>{literature.current ? 'Yes' : 'No'}</span>
            </S.CurrentCell>

            <S.PublishedAtAndDownloadButton>
              <div>{literature.datePublished}</div>
              <Tooltip title={downloadingId === literature.documentName ? 'Downloading...' : 'Click to download'} placement="top">
                {downloadingId === literature.documentName ? (
                  <S.LoadingSpinner variant="cell" />
                ) : (
                  <S.DownloadIcon
                    src={'/img/download_icon.svg'}
                    alt={'literature-download-icon'}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        onDownloadPdfClick(literature)
                      }
                    }}
                    onClick={() => onDownloadPdfClick(literature)}
                  />
                )}
              </Tooltip>
            </S.PublishedAtAndDownloadButton>
          </S.Item>
        ))}

        {group.literature.length > 10 &&
          displayedLiterature.length < group.literature.length && (
            <Button
              text="Show more"
              onPress={onClickShowMore}
              styles={showMoreButtonStyle}
            />
        )}
      </S.CollapsiblePanel>
    </div>
  )
}

export default ProductLiteratureGroup

const showMoreButtonStyle: ButtonStyles = {
  button: {
    backgroundColor: 'transparent',
    borderColor: 'transparent',
    marginLeft: 'auto',
    marginRight: 'auto',
    marginTop: 10
  },
  text: {
    textDecorationLine: 'none',
    textTransform: 'none',
    color: color('warrantyText')
  }
}

const mediumCellStyle = css`
  @media ${screenSize.medium} {
    width: 100%;
    font-size: 12px;
  }
`

const S = {
  CollapsiblePanel: styled(CollapsiblePanel)<{
    page: 'search' | 'deviceDetails'
    isOpen: boolean
  }>`
    margin-bottom: 3px;
    ${(props) => props.page === 'search' ? `
      background: ${props.isOpen ? color('customerFilterGroup') : color('paneBackground')};
      margin-left: 10px;
      margin-right: 10px;
      line-height: 1;
      border-radius: 16px;

      @media ${screenSize.medium} {
        line-height: 1.5;
      }
  `
    : `
      background: ${color('background')};
      padding-left: 10px;
  `}
  `,
  GroupHeader: styled.div<{
    page: 'search' | 'deviceDetails'
    $isOpen: boolean
  }>`
    background: ${(props) =>
    props.page === 'search'
      ? `${props.$isOpen ? color('customerFilterGroup') : color('paneBackground')}`
      : `${color('background')}`};
    padding: 15px 15px 15px 0px;
    border-radius: 16px;
    font-weight: 500;
    color: ${color('warrantyText')};
  `,
  NameHeaderCell: styled(Table.HeaderCell)`
    flex-basis: 25%;
    ${mediumCellStyle}
  `,
  DescriptionHeaderCell: styled(Table.HeaderCell)`
    flex-basis: 40%;
    ${mediumCellStyle}
  `,
  LanguageHeaderCell: styled(Table.HeaderCell)`
    flex-basis: 15%;
    ${mediumCellStyle}
  `,
  CurrentHeaderCell: styled(Table.HeaderCell)`
    flex-basis: 7%;
    ${mediumCellStyle}
    padding-right: 15px;

    @media ${screenSize.medium} {
      padding: 0 5px;
    }
  `,
  DateHeaderCell: styled(Table.HeaderCell)`
    flex-basis: 20%;
    ${mediumCellStyle}
  `,
  TableHeader: styled(Table.Header)`
    margin: 10px;
  `,
  Item: styled.div`
    display: flex;
    position: relative;
    padding: 10px;
    align-items: center;
    border-bottom: 1px solid ${color('dashboardCardBorder')};
    margin: 0 1rem;

    @media ${screenSize.medium} {
      align-items: flex-start;
      margin: 0;
    }
  `,
  Link: styled.a`
    display: inline-flex;
    align-items: center;
    color: ${color('warrantyText')};
    font-weight: 500;
    text-decoration: none;

    &:hover {
      color: ${color('warrantyText')};
    }
  `,
  PictureAsPdfOutlined: styled.img`
    padding-right: 10px;
    padding-left: 1rem;
    
    @media ${screenSize.medium} {
      width: 25px;
      height: 25px;
      padding: 0 2px 0 0;
    }
  `,
  NameCell: styled.div`
    flex-basis: 25%;
    ${mediumCellStyle}
  `,
  DescriptionCell: styled.div`
    flex-basis: 40%;
    color: ${color('primaryText')};
    line-height: 1.6;
    ${mediumCellStyle}
  `,
  LanguageCell: styled.div`
    flex-basis: 15%;
    padding-left: 2rem;
    ${mediumCellStyle}

  @media ${screenSize.medium} {
    padding-left: 10px;
  }
  `,
  CurrentCell: styled.div`
    flex-basis: 5%;
    padding-left: 2rem;
    ${mediumCellStyle}

    @media ${screenSize.medium} {
      padding-left: 0;
    }
  `,
  PublishedAtAndDownloadButton: styled.div`
    flex-basis: 20%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-left: 2rem;
    ${mediumCellStyle}
    
    @media ${screenSize.medium} {
      padding-left: 0;
      justify-content: space-evenly;
    }
  `,
  DownloadIcon: styled.img`
    width: 20px;
    height: 20px;
    cursor: pointer;

    @media ${screenSize.medium} {
      width: 15px;
      height: 15px;
    }
  `,
  LoadingSpinner: styled(spinner)`
    width: 20px;
    height: 20px;
    border: 2px solid ${color('dashboardCardBorder')};
    border-top-color: ${color('warrantyText')};

    @media ${screenSize.medium} {
      width: 15px;
      height: 15px;
    }
  `
}
